import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Code,
  Zap,
  Palette,
  BrainCircuit,
  MessageSquare,
  Image,
  Star,
  ExternalLink
} from 'lucide-react';

const AITools = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');

  const categories = [
    { name: '全部', icon: Zap, count: 48 },
    { name: 'AI创作', icon: Palette, count: 12 },
    { name: '商业应用', icon: BrainCircuit, count: 15 },
    { name: '开发工具', icon: Code, count: 8 },
    { name: '学习资源', icon: MessageSquare, count: 6 },
    { name: '图像处理', icon: Image, count: 7 }
  ];

  const allTools = [
    // AI创作工具
    {
      id: 1,
      name: 'ChatGPT',
      description: '目前最受欢迎的AI对话助手，支持文本生成、代码编写、问题解答等多种功能',
      category: 'AI创作',
      url: 'https://chat.openai.com',
      rating: 4.8,
      users: '100M+',
      pricing: 'Freemium',
      features: ['对话生成', '代码编写', '文本创作', '问题解答'],
      logo: '🤖',
      trending: true
    },
    {
      id: 2,
      name: 'Midjourney',
      description: '顶级AI图像生成工具，能够根据文字描述创造出惊艳的艺术作品和插图',
      category: 'AI创作',
      url: 'https://midjourney.com',
      rating: 4.7,
      users: '20M+',
      pricing: '$10/月',
      features: ['图像生成', '艺术创作', '插图设计', '概念图'],
      logo: '🎨',
      trending: true
    },
    {
      id: 3,
      name: 'Claude',
      description: 'Anthropic开发的高级AI助手，在安全性和有用性方面表现卓越',
      category: 'AI创作',
      url: 'https://claude.ai',
      rating: 4.6,
      users: '5M+',
      pricing: 'Freemium',
      features: ['长文本处理', '分析能力', '安全对话', '文档处理'],
      logo: '🧠',
      trending: false
    },
    {
      id: 4,
      name: 'Stable Diffusion',
      description: '开源的AI图像生成模型，可以本地部署，自由度更高',
      category: 'AI创作',
      url: 'https://stability.ai',
      rating: 4.5,
      users: '15M+',
      pricing: '免费',
      features: ['开源', '本地部署', '图像生成', '模型微调'],
      logo: '🌟',
      trending: false
    },

    // 商业应用
    {
      id: 5,
      name: 'Notion AI',
      description: '集成在Notion中的AI助手，帮助提高写作和组织效率',
      category: '商业应用',
      url: 'https://notion.so',
      rating: 4.4,
      users: '30M+',
      pricing: '$10/月',
      features: ['文档写作', '内容总结', '翻译', '头脑风暴'],
      logo: '📝',
      trending: false
    },
    {
      id: 6,
      name: 'Copy.ai',
      description: '专业的AI文案生成工具，适合营销和内容创作者',
      category: '商业应用',
      url: 'https://copy.ai',
      rating: 4.3,
      users: '10M+',
      pricing: '$49/月',
      features: ['营销文案', '社媒内容', '邮件营销', '广告创意'],
      logo: '✍️',
      trending: false
    },
    {
      id: 7,
      name: 'Jasper',
      description: '企业级AI写作助手，专注于营销内容和品牌一致性',
      category: '商业应用',
      url: 'https://jasper.ai',
      rating: 4.2,
      users: '8M+',
      pricing: '$59/月',
      features: ['品牌语调', '团队协作', '模板库', 'SEO优化'],
      logo: '🚀',
      trending: false
    },

    // 开发工具
    {
      id: 8,
      name: 'GitHub Copilot',
      description: '由OpenAI Codex驱动的AI编程助手，大幅提升编码效率',
      category: '开发工具',
      url: 'https://github.com/features/copilot',
      rating: 4.5,
      users: '5M+',
      pricing: '$10/月',
      features: ['代码补全', '函数生成', '注释解释', '多语言支持'],
      logo: '💻',
      trending: true
    },
    {
      id: 9,
      name: 'Cursor',
      description: '专为AI编程设计的IDE，提供强大的AI辅助功能',
      category: '开发工具',
      url: 'https://cursor.sh',
      rating: 4.6,
      users: '1M+',
      pricing: '$20/月',
      features: ['AI聊天', '代码编辑', '智能重构', '错误修复'],
      logo: '⚡',
      trending: true
    },

    // 学习资源
    {
      id: 10,
      name: 'Perplexity',
      description: 'AI驱动的搜索引擎，提供准确的答案和来源引用',
      category: '学习资源',
      url: 'https://perplexity.ai',
      rating: 4.4,
      users: '3M+',
      pricing: 'Freemium',
      features: ['智能搜索', '来源引用', '实时信息', '多语言'],
      logo: '🔍',
      trending: true
    },
    {
      id: 11,
      name: 'Hugging Face',
      description: '最大的AI模型社区，提供各种预训练模型和工具',
      category: '学习资源',
      url: 'https://huggingface.co',
      rating: 4.7,
      users: '2M+',
      pricing: 'Freemium',
      features: ['模型库', '数据集', '社区', '云端推理'],
      logo: '🤗',
      trending: false
    },

    // 图像处理
    {
      id: 12,
      name: 'Remove.bg',
      description: 'AI驱动的背景移除工具，一键抠图',
      category: '图像处理',
      url: 'https://remove.bg',
      rating: 4.3,
      users: '50M+',
      pricing: 'Freemium',
      features: ['背景移除', '批量处理', 'API接口', '高质量输出'],
      logo: '✂️',
      trending: false
    }
  ];

  const filteredTools = allTools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '全部' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">🚀 AI工具导航</h1>
          <p className="text-xl text-gray-600 mb-8">精选全球最受欢迎的AI工具，助力您的工作和创作</p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索AI工具..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ai-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all ${
                selectedCategory === category.name
                  ? 'bg-ai-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-ai-50 border border-gray-200'
              }`}
            >
              <category.icon className="w-4 h-4" />
              <span>{category.name}</span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {category.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              {/* Card Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">{tool.logo}</div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-ai-600 transition-colors">
                        {tool.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          <span>{tool.rating}</span>
                        </span>
                        <span>•</span>
                        <span>{tool.users} 用户</span>
                      </div>
                    </div>
                  </div>
                  {tool.trending && (
                    <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                      🔥 热门
                    </span>
                  )}
                </div>

                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {tool.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {tool.features.slice(0, 3).map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                    >
                      {feature}
                    </span>
                  ))}
                  {tool.features.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      +{tool.features.length - 3}
                    </span>
                  )}
                </div>

                {/* Pricing & Action */}
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-gray-500">价格: </span>
                    <span className="font-medium text-ai-600">{tool.pricing}</span>
                  </div>
                  <a
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 bg-ai-600 text-white px-4 py-2 rounded-lg hover:bg-ai-700 transition-colors text-sm font-medium"
                  >
                    <span>体验</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredTools.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">没有找到相关工具</h3>
            <p className="text-gray-600">试试其他搜索词或选择不同的分类</p>
          </motion.div>
        )}

        {/* Submit Tool CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-ai-600 to-primary-600 rounded-2xl p-8 text-center text-white"
        >
          <h3 className="text-2xl font-bold mb-4">推荐优秀的AI工具</h3>
          <p className="text-ai-100 mb-6">
            发现了好用的AI工具？告诉我们，让更多人受益！
          </p>
          <button className="bg-white text-ai-600 px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all">
            推荐工具
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default AITools; 